import { CATALOGO_CONQUISTAS, CATALOGO_LOJA, COMPETICOES_CONQUISTA, aplicarItemLoja, itemDaLoja, itemJaAtivo, progressoRegra, type DefConquista, type EstatConta } from '../core';
import { app, humano, salvarLocal } from './app';
import { dialogo } from './dialogos';
import { icone } from './icones';
import { cancelarConfirmacao, chamarApi, confirmacaoDaConta, confirmarEmailDaConta, enviar, entrarNaConta, htmlCampoEmail, htmlConfirmacaoEmail, reenviarCodigoDaConta, sairDaConta, sessaoDaConta } from './online';
import { avisoNaTela } from './notificacoes';
import { somConquista } from './som';
import { esc } from './util';

/**
 * Conta do jogador: a "fotinha" no canto superior direito (perfil, informações da conta e conquistas) e a loja de WebCoins.
 * Tudo vem do servidor (o saldo e as conquistas só o servidor altera); sem conta, as janelas oferecem entrar ou criar uma.
 */

interface PerfilUi {
  usuario: string;
  criadaEm: number;
  foto: string | null;
  webcoins: number;
  extrato: { t: number; valor: number; motivo: string }[];
  conquistas: { id: string; temporada?: number; detalhe?: string; quando: number }[];
  temSave: boolean;
  saveEm: number | null;
  salas: number;
  /** catálogo vindo do servidor (inclui as conquistas criadas no painel admin) */
  catalogo?: DefConquista[];
  estat?: EstatConta;
  nivel?: { nivel: number; xp: number; xpInicio: number; xpProximo: number };
  /** só preenchido na resposta que pagou o bônus de login do dia */
  bonusDiario?: { diaDoCiclo: number; valor: number; sequencia: number } | null;
  sequenciaLogin?: { sequencia: number; diaDoCiclo: number };
  convite?: { codigo: string; convidados: string[]; premio: number; maximo: number };
  pedidosAmizade?: number;
}

interface LinhaAmigoUi {
  usuario: string;
  foto: string | null;
  nivel: number;
  conquistas: number;
}
interface AmigosUi {
  amigos: LinhaAmigoUi[];
  recebidos: LinhaAmigoUi[];
  enviados: LinhaAmigoUi[];
  maximo: number;
}
let amigos: AmigosUi | null = null;

async function carregarAmigos(): Promise<void> {
  const s = sessaoDaConta();
  if (!s) return;
  try {
    amigos = await chamarApi<AmigosUi>(`/conta/amigos?token=${encodeURIComponent(s.token)}`);
    if (perfil) perfil.pedidosAmizade = amigos.recebidos.length;
  } catch (erro) {
    app.mensagem = texto(erro);
  }
}

async function acaoAmigo(acao: string, usuario: string): Promise<void> {
  const s = sessaoDaConta();
  if (!s) return;
  try {
    amigos = await chamarApi<AmigosUi>('/conta/amigos', { token: s.token, acao, usuario });
    if (perfil) perfil.pedidosAmizade = amigos.recebidos.length;
    app.mensagem =
      acao === 'pedir' ? (amigos.amigos.some((a) => a.usuario.toLowerCase() === usuario.toLowerCase()) ? `${usuario} agora é seu amigo!` : `Pedido enviado para ${usuario}.`) : acao === 'aceitar' ? `${usuario} agora é seu amigo!` : acao === 'remover' ? `${usuario} saiu da sua lista de amigos.` : '';
  } catch (erro) {
    app.mensagem = texto(erro);
  }
  desenharTela();
}

let perfil: PerfilUi | null = null;

/** O perfil da conta como o servidor devolveu da última vez (usado pelas notificações de nível e conquistas). */
export function perfilAtual(): PerfilUi | null {
  return perfil;
}
let aba: 'resumo' | 'conquistas' | 'foto' | 'convite' | 'amigos' = 'resumo';
let desenharTela: () => void = () => undefined;

export function ligarConta(desenhar: () => void): void {
  desenharTela = desenhar;
}

const texto = (erro: unknown): string => (erro instanceof Error ? erro.message : String(erro));

/** Busca o perfil da conta logada (ou limpa, se não há conta ou a sessão acabou). */
export async function carregarPerfil(): Promise<void> {
  const s = sessaoDaConta();
  if (!s) {
    perfil = null;
    return;
  }
  try {
    perfil = await chamarApi<PerfilUi>(`/conta/perfil?token=${encodeURIComponent(s.token)}`);
    const b = perfil.bonusDiario;
    if (b) {
      // primeiro acesso do dia: o servidor acabou de pagar o bônus
      avisoNaTela('🎁', `Bônus de login: +${b.valor} WebCoin${b.valor > 1 ? 's' : ''}`, b.diaDoCiclo === 7 ? 'Sétimo dia seguido: prêmio máximo! O ciclo recomeça amanhã.' : `Dia ${b.diaDoCiclo} de 7 — no sétimo dia seguido o prêmio é de 50 WebCoins.`);
      somConquista();
    }
  } catch (erro) {
    // sessão vencida: some o perfil e a pessoa entra de novo; falha de rede mantém o que já havia
    if (/sess[ãa]o|entre na sua conta/i.test(texto(erro))) perfil = null;
  }
}

// ---- avatar ---------------------------------------------------------------------------------------------

/** Avatares prontos (a foto "preset:N" guarda só o número). */
const AVATARES: { emoji: string; cor: string }[] = [
  { emoji: '⚽', cor: '#2e7d32' },
  { emoji: '🧤', cor: '#1565c0' },
  { emoji: '🏆', cor: '#b8860b' },
  { emoji: '🦁', cor: '#c77d1a' },
  { emoji: '🐯', cor: '#d35400' },
  { emoji: '🦅', cor: '#455a64' },
  { emoji: '🐺', cor: '#546e7a' },
  { emoji: '🐉', cor: '#6a1b9a' },
  { emoji: '🔥', cor: '#c62828' },
  { emoji: '⭐', cor: '#f9a825' },
  { emoji: '👑', cor: '#8d6e63' },
  { emoji: '🎯', cor: '#ad1457' },
  { emoji: '🦈', cor: '#00838f' },
  { emoji: '🐍', cor: '#558b2f' },
  { emoji: '🦊', cor: '#e65100' },
  { emoji: '🐻', cor: '#4e342e' },
  { emoji: '🚀', cor: '#283593' },
  { emoji: '⚡', cor: '#f57f17' },
  { emoji: '🧠', cor: '#6d4c41' },
  { emoji: '🎩', cor: '#212121' },
];

const COR_INICIAL = ['#2e7d32', '#1565c0', '#6a1b9a', '#c62828', '#00838f', '#ef6c00', '#ad1457', '#37474f'];

function corDoNome(nome: string): string {
  let h = 0;
  for (const c of nome) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return COR_INICIAL[h % COR_INICIAL.length] as string;
}

/** A foto da conta: imagem enviada, avatar pronto ou a inicial do usuário numa cor fixa. */
export function avatarHtml(foto: string | null | undefined, usuario: string, px = 40): string {
  const tam = `width:${px}px;height:${px}px;font-size:${Math.round(px * 0.5)}px`;
  if (foto?.startsWith('data:image/')) return `<img class="avatar" style="${tam}" src="${esc(foto)}" alt="" />`;
  const n = foto?.startsWith('preset:') ? Number(foto.slice(7)) : -1;
  const a = Number.isInteger(n) ? AVATARES[n] : undefined;
  if (a) return `<span class="avatar" style="${tam};background:${a.cor}" aria-hidden="true">${a.emoji}</span>`;
  const inicial = (usuario.trim()[0] ?? '?').toUpperCase();
  return `<span class="avatar" style="${tam};background:${corDoNome(usuario)}" aria-hidden="true">${esc(inicial)}</span>`;
}

// ---- botões do canto superior direito ---------------------------------------------------------------------

const fmtCoins = (n: number): string => n.toLocaleString('pt-BR');

/** Loja (com o saldo) e a fotinha da conta, no canto superior direito. */
export function botoesConta(): string {
  const s = sessaoDaConta();
  const saldo = s && perfil ? `<span class="coin-saldo">${fmtCoins(perfil.webcoins)}</span>` : '';
  const foto = s ? avatarHtml(perfil?.foto, s.usuario, 30) : `<span class="avatar vazio" style="width:30px;height:30px" aria-hidden="true">👤</span>`;
  return `<div class="bf-conta">
    <button class="bf-loja" data-a="abrir" data-d="loja" title="Loja e WebCoins" aria-label="Loja e WebCoins">${icone('loja', 22)}${saldo}</button>
    <button class="bf-perfil" data-a="abrir" data-d="conta" title="${s ? `Conta de ${esc(s.usuario)}` : 'Entrar na conta'}" aria-label="Minha conta">${foto}</button>
  </div>`;
}

// ---- janelas ----------------------------------------------------------------------------------------------

const dataBR = (t: number): string => new Date(t).toLocaleDateString('pt-BR');
const dataHoraBR = (t: number): string => new Date(t).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });

function formularioEntrar(titulo: string, motivo: string): string {
  if (confirmacaoDaConta()) return `<div class="conta-entrar">${htmlConfirmacaoEmail()}</div>`;
  return `<div class="conta-entrar">
    <h2>${esc(titulo)}</h2>
    <p>${esc(motivo)}</p>
    <div class="conta-linha">
      <input type="text" id="cx-usuario" maxlength="24" placeholder="usuário" autocomplete="username" />
      <input type="password" id="cx-senha" maxlength="100" placeholder="senha (mín. 6)" autocomplete="current-password" />
      ${htmlCampoEmail('cx-email')}
      <button class="btn principal" data-a="conta-entrar">Entrar</button>
      <button class="btn" data-a="conta-registrar">Criar conta</button>
    </div>
    <p class="cinza peq">A conta é gratuita e opcional: guarda seu perfil, suas conquistas, suas WebCoins, suas salas online e o save do jogo na nuvem.</p>
  </div>`;
}

const mensagem = (): string => (app.mensagem ? `<div class="aviso-bf dentro" role="alert">${esc(app.mensagem)}</div>` : '');

function abaResumo(p: PerfilUi): string {
  const total = CATALOGO_CONQUISTAS.length;
  const tipos = new Set(p.conquistas.map((c) => c.id)).size;
  const quadro = (rotulo: string, valor: string, nota = ''): string =>
    `<div class="conta-tile"><span class="cinza peq">${rotulo}</span><b>${valor}</b>${nota ? `<span class="cinza peq">${nota}</span>` : ''}</div>`;
  const n = p.nivel;
  const feito = n ? n.xp - n.xpInicio : 0;
  const total_n = n ? n.xpProximo - n.xpInicio : 1;
  const nivel = n
    ? `<div class="conta-nivel">
        <div class="conta-nivel-cab"><b>Nível ${n.nivel}</b><span class="cinza peq">${n.xp} XP no total</span></div>
        <div class="conq-prog alta" role="img" aria-label="${feito} de ${total_n} XP"><i style="width:${Math.round((100 * feito) / total_n)}%"></i><b>${feito} / ${total_n} XP</b></div>
        <span class="cinza peq">Faltam ${n.xpProximo - n.xp} XP para o nível ${n.nivel + 1}.${n.nivel < 1 ? ' Contas no nível 0 não aparecem no Ranking.' : ''}</span>
      </div>`
    : '';
  const sl = p.sequenciaLogin;
  const dia = sl?.diaDoCiclo ?? 0;
  const pontos = Array.from({ length: 7 }, (_, i) => `<span class="login-dia${i + 1 <= dia ? ' ok' : ''}${i === 6 ? ' premio' : ''}" title="Dia ${i + 1}${i === 6 ? ': 50 WebCoins' : ': 1 WebCoin'}">${i === 6 ? '50' : i + 1}</span>`).join('');
  const login = `<div class="conta-login"><div class="conta-nivel-cab"><b>Bônus de login</b><span class="cinza peq">${dia > 0 ? `${sl?.sequencia ?? 0} dia${(sl?.sequencia ?? 0) > 1 ? 's' : ''} seguido${(sl?.sequencia ?? 0) > 1 ? 's' : ''}` : ''}</span></div>
    <div class="login-dias">${pontos}</div><span class="cinza peq">1 WebCoin por dia; no 7º dia seguido, 50. Faltar um dia recomeça a contagem.</span></div>`;
  return `${nivel}
  <div class="conta-tiles">
    ${quadro('WebCoins', fmtCoins(p.webcoins))}
    ${quadro('Conquistas', String(p.conquistas.length), `${tipos} de ${total} tipos`)}
    ${quadro('Salas online', String(p.salas))}
  </div>
  ${login}
  <p class="cinza peq conta-save">Save do jogo na nuvem: ${p.temSave && p.saveEm ? dataHoraBR(p.saveEm) : 'nenhum'}</p>
  <div class="dlg-botoes"><button class="btn" data-a="conta-sair">Sair da conta</button></div>`;
}

function abaConquistas(p: PerfilUi): string {
  const catalogo = p.catalogo ?? [...CATALOGO_CONQUISTAS];
  const cards = catalogo.map((def) => {
    const ganhas = p.conquistas.filter((c) => c.id === def.id);
    if (ganhas.length === 0) {
      const pr = def.regra ? progressoRegra(def.regra, p.estat) : null;
      const barra = pr ? `<div class="conq-prog" role="img" aria-label="${pr.atual} de ${pr.alvo}"><i style="width:${Math.round((100 * pr.atual) / pr.alvo)}%"></i><b>${pr.atual}/${pr.alvo}</b></div>` : '';
      return `<div class="conq bloqueada"><span class="conq-ico">🔒</span><div><b>${esc(def.titulo)}</b><br><span class="cinza peq">${esc(def.descricao)}</span>${barra}</div></div>`;
    }
    const onde = ganhas.map((g) => `${g.temporada !== undefined ? `temporada ${g.temporada}` : dataBR(g.quando)}${g.detalhe ? ` · ${esc(g.detalhe)}` : ''}`).join('<br>');
    return `<div class="conq"><span class="conq-ico">${def.icone}</span><div><b>${esc(def.titulo)}${ganhas.length > 1 ? ` ×${ganhas.length}` : ''}</b><br><span class="cinza peq">${esc(def.descricao)}</span><br><span class="peq">${onde}</span></div></div>`;
  }).join('');
  // conquistas de um tipo que saiu do catálogo continuam aparecendo
  const extras = p.conquistas.filter((c) => !catalogo.some((x) => x.id === c.id)).map((c) => `<div class="conq"><span class="conq-ico">🏅</span><div><b>${esc(c.id)}</b></div></div>`).join('');
  return `<p class="cinza peq">As conquistas são dadas pelo servidor, só em salas online com a conta logada: as de partidas ao apito final de cada jogo (competições: ) e as de título quando a temporada é encerrada.</p>
    <div class="conq-lista">${cards}${extras}</div>`;
}

function abaAmigos(): string {
  if (!amigos) return '<p class="cinza">Carregando seus amigos…</p>';
  const linha = (a: LinhaAmigoUi, botoes: string): string =>
    `<li class="amigo">${avatarHtml(a.foto, a.usuario, 36)}<div class="amigo-nome"><b>${esc(a.usuario)}</b><span class="cinza peq">Nível ${a.nivel} · ${a.conquistas} conquista${a.conquistas === 1 ? '' : 's'}</span></div><div class="amigo-acoes">${botoes}</div></li>`;
  const btn = (acao: string, usuario: string, rotulo: string, principal = false): string =>
    `<button class="btn peq${principal ? ' principal' : ''}" data-a="conta-amigo" data-acao="${acao}" data-u="${esc(usuario)}">${rotulo}</button>`;
  const recebidos = amigos.recebidos.length
    ? `<h3 class="amigos-tit">Pedidos recebidos (${amigos.recebidos.length})</h3><ul class="amigos-lista">${amigos.recebidos.map((a) => linha(a, `${btn('aceitar', a.usuario, 'Aceitar', true)}${btn('recusar', a.usuario, 'Recusar')}`)).join('')}</ul>`
    : '';
  const lista = amigos.amigos.length
    ? `<ul class="amigos-lista">${amigos.amigos.map((a) => linha(a, btn('remover', a.usuario, 'Remover'))).join('')}</ul>`
    : '<p class="cinza peq">Você ainda não tem amigos adicionados. Procure um técnico pelo nome de usuário acima.</p>';
  const enviados = amigos.enviados.length
    ? `<h3 class="amigos-tit">Pedidos enviados (${amigos.enviados.length})</h3><ul class="amigos-lista">${amigos.enviados.map((a) => linha(a, btn('cancelar', a.usuario, 'Cancelar'))).join('')}</ul>`
    : '';
  return `<div class="conta-linha"><input type="text" id="amigo-novo" maxlength="24" placeholder="usuário do técnico" autocomplete="off" />
      <button class="btn principal" data-a="conta-amigo-pedir">Adicionar amigo</button></div>
    ${recebidos}
    <h3 class="amigos-tit">Meus amigos (${amigos.amigos.length}/${amigos.maximo})</h3>${lista}${enviados}
    <p class="cinza peq">A amizade só vale quando a outra pessoa aceita o pedido. Não achou o amigo? Mande seu link da aba Convidar.</p>`;
}

/** Endereço do link de convite desta conta. */
function linkDeConvite(codigo: string): string {
  return `${location.origin}/?convite=${encodeURIComponent(codigo)}`;
}

function abaConvite(p: PerfilUi): string {
  const c = p.convite;
  if (!c) return '<p class="cinza">Carregando…</p>';
  const lista = c.convidados.length
    ? `<ul class="convidados">${[...c.convidados].reverse().map((u) => `<li>${esc(u)} <span class="cinza peq">+${c.premio} WebCoins</span></li>`).join('')}</ul>`
    : '<p class="cinza peq">Ninguém usou o seu link ainda.</p>';
  return `<p>Chame outros técnicos: quando alguém <b>criar uma conta</b> pelo seu link, você ganha <b>${c.premio} WebCoins</b> (até ${c.maximo} convites premiados).</p>
    <div class="conta-linha"><input type="text" id="conta-link-convite" value="${esc(linkDeConvite(c.codigo))}" readonly aria-label="Seu link de convite" />
      <button class="btn principal" data-a="conta-copiar-convite">Copiar link</button></div>
    <p class="cinza peq">Convidados que já criaram conta: ${c.convidados.length}${c.convidados.length ? ` · ${fmtCoins(c.convidados.length * c.premio)} WebCoins ganhas` : ''}</p>
    ${lista}`;
}

function abaFoto(p: PerfilUi): string {
  const grade = AVATARES.map((a, i) => `<button class="avatar-op${p.foto === `preset:${i}` ? ' sel' : ''}" data-a="conta-foto-preset" data-i="${i}" aria-label="Avatar ${i + 1}"><span class="avatar" style="width:44px;height:44px;font-size:22px;background:${a.cor}">${a.emoji}</span></button>`).join('');
  return `<p>Escolha um avatar ou envie a sua foto (ela é reduzida para 96×96).</p>
    <div class="avatar-grade">${grade}</div>
    <div class="dlg-botoes">
      <input type="file" id="conta-foto-arquivo" accept="image/*" hidden />
      <button class="btn" data-a="conta-foto-enviar">Enviar uma foto…</button>
      <button class="btn" data-a="conta-foto-remover" ${p.foto ? '' : 'disabled'}>Usar a inicial do nome</button>
    </div>`;
}

export function dlgConta(): string {
  const s = sessaoDaConta();
  if (!s) return dialogo('Minha conta', `${formularioEntrar('Minha conta', 'Entre para ver o seu perfil, as suas conquistas e as suas WebCoins.')}${mensagem()}`, 'conta-dlg');
  if (!perfil) return dialogo('Minha conta', `<p class="cinza">Carregando o perfil de ${esc(s.usuario)}…</p>${mensagem()}`, 'conta-dlg');
  const p = perfil;
  const abas: [typeof aba, string][] = [
    ['resumo', 'Informações'],
    ['conquistas', `Conquistas (${p.conquistas.length})`],
    ['foto', 'Foto'],
    ['amigos', `Amigos${p.pedidosAmizade ? ` 🔴${p.pedidosAmizade}` : ''}`],
    ['convite', 'Convidar'],
  ];
  const corpo = aba === 'conquistas' ? abaConquistas(p) : aba === 'foto' ? abaFoto(p) : aba === 'convite' ? abaConvite(p) : aba === 'amigos' ? abaAmigos() : abaResumo(p);
  return dialogo(
    'Minha conta',
    `<div class="conta-cab">${avatarHtml(p.foto, p.usuario, 64)}<div><h2>${esc(p.usuario)}</h2><div class="cinza peq">Nível ${p.nivel?.nivel ?? 0} · conta criada em ${dataBR(p.criadaEm)}</div></div>
      <button class="coin-chip" data-a="abrir" data-d="loja" title="Abrir a loja">${icone('moeda', 20)}<b>${fmtCoins(p.webcoins)}</b> WebCoins</button></div>
    <div class="abas-esc">${abas.map(([k, nome]) => `<button type="button" class="btn${aba === k ? ' principal' : ''}" data-a="conta-aba" data-p="${k}">${esc(nome)}</button>`).join('')}</div>
    <div class="conta-corpo">${corpo}</div>${mensagem()}`,
    'conta-dlg',
  );
}

// ---- ranking: todas as contas do servidor -----------------------------------------------------------------

interface LinhaRanking {
  usuario: string;
  criadaEm: number;
  foto: string | null;
  webcoins: number;
  conquistas: number;
  nivel: number;
  xp: number;
  ultimaConquista: { id: string; titulo: string; icone: string; quando: number } | null;
  salas: number;
  voce: boolean;
}

let ranking: LinhaRanking[] | null = null;
let falhaRanking = '';

/** Busca a lista de contas do servidor (qualquer pessoa vê; a sua linha vem destacada quando há conta logada). */
export async function carregarRanking(): Promise<void> {
  const s = sessaoDaConta();
  try {
    const r = await chamarApi<{ contas: LinhaRanking[] }>(`/ranking${s ? `?token=${encodeURIComponent(s.token)}` : ''}`);
    ranking = r.contas;
    falhaRanking = '';
  } catch (erro) {
    falhaRanking = texto(erro);
  }
}

export function dlgRanking(): string {
  if (!ranking) return dialogo('Ranking', `<p class="${falhaRanking ? 'erro' : 'cinza'}">${falhaRanking ? `Não foi possível carregar o ranking: ${esc(falhaRanking)}` : 'Carregando o ranking…'}</p><div class="dlg-botoes"><button class="btn" data-a="fechar">Fechar</button></div>`, 'medio');
  // maior nível (mais XP) primeiro; empate: mais conquistas, mais WebCoins e a conta mais antiga
  const lista = [...ranking].sort((a, b) => b.xp - a.xp || b.conquistas - a.conquistas || b.webcoins - a.webcoins || a.criadaEm - b.criadaEm);
  const linhas = lista
    .map((c, i) => {
      const ultima = c.ultimaConquista ? `${esc(c.ultimaConquista.icone)} ${esc(c.ultimaConquista.titulo)} <span class="cinza peq">${dataBR(c.ultimaConquista.quando)}</span>` : '<span class="cinza">–</span>';
      return `<tr class="${c.voce ? 'proxima' : ''}"><td>${i + 1}</td><td class="esq rk-nome">${avatarHtml(c.foto, c.usuario, 26)}<b>${esc(c.usuario)}</b>${c.voce ? ' <span class="cinza peq">(você)</span>' : ''}</td><td><b>${c.nivel}</b> <span class="cinza peq">${c.xp} XP</span></td><td>${c.conquistas}</td><td>${fmtCoins(c.webcoins)}</td><td>${c.salas}</td><td class="esq">${ultima}</td><td>${dataBR(c.criadaEm)}</td></tr>`;
    })
    .join('');
  return dialogo(
    'Ranking',
    `<div class="dlg-tit amarelo">Ranking de técnicos · ${lista.length} ${lista.length === 1 ? 'conta' : 'contas'}</div>
    <div class="dlg-corpo rolavel alta"><table class="tab-dlg"><thead><tr><th>#</th><th class="esq">Técnico</th><th>Nível</th><th>Conquistas</th><th>WebCoins</th><th>Salas</th><th class="esq">Última conquista</th><th>Desde</th></tr></thead><tbody>${linhas || '<tr><td colspan="8" class="cinza">Ninguém chegou ao nível 1 ainda (10 XP: cada vitória online vale 10, contra outro técnico 20, e cada título 250).</td></tr>'}</tbody></table></div>
    <div class="dlg-botoes"><button class="btn" data-a="fechar">Fechar</button></div>`,
    'grande',
  );
}

/** Vitrine: cada item mostra o que dá e só compra com um jogo aberto (o efeito vale para o clube do técnico) e saldo suficiente. */
function itensDaLoja(saldo: number): string {
  const temJogo = Boolean(app.jogo);
  const cartoes = CATALOGO_LOJA.map((i) => {
    const semSaldo = saldo < i.preco;
    const ativo = temJogo && itemJaAtivo(humano(), i);
    const motivo = !temJogo ? 'Abra um jogo para comprar' : ativo ? 'Já vale para a próxima partida' : semSaldo ? 'WebCoins insuficientes' : '';
    return `<div class="loja-item"><div class="loja-item-ico">${icone('moeda', 34)}</div>
      <div class="loja-item-txt"><b>${esc(i.nome)}</b><span>${esc(i.descricao)}</span><span class="loja-ganho">${esc(i.ganho)}</span></div>
      <div class="loja-item-preco"><button class="btn principal" data-a="conta-comprar" data-id="${esc(i.id)}" ${motivo ? 'disabled' : ''}>${icone('moeda', 16)} ${fmtCoins(i.preco)}</button>${motivo ? `<span class="cinza peq">${motivo}</span>` : ''}</div></div>`;
  }).join('');
  return `<div class="loja-itens">${cartoes}</div>`;
}

let comprando = false;

/** Compra pelo servidor, que cobra as WebCoins; no jogo solo o efeito é aplicado aqui, no online o próprio servidor aplica no clube. */
async function comprarItem(id: string): Promise<void> {
  const s = sessaoDaConta();
  const item = itemDaLoja(id);
  if (!s || !item || comprando) return;
  if (!app.jogo) {
    app.mensagem = 'Abra um jogo para usar este item.';
    return;
  }
  if (itemJaAtivo(humano(), item)) {
    app.mensagem = 'Esse item já está valendo para a próxima partida.';
    desenharTela();
    return;
  }
  comprando = true;
  try {
    if (app.online) await enviar({ tipo: 'loja-comprar', item: id });
    else {
      await chamarApi('/conta/loja', { token: s.token, item: id });
      aplicarItemLoja(humano(), item);
      salvarLocal();
    }
    await carregarPerfil();
    app.mensagem = `Comprado: ${item.nome} (${item.ganho}).`;
  } catch (erro) {
    app.mensagem = texto(erro);
  }
  comprando = false;
  desenharTela();
}

export function dlgLoja(): string {
  const s = sessaoDaConta();
  if (!s) return dialogo('Loja WebFoot', `${formularioEntrar('Loja WebFoot', 'Entre na sua conta para ver as suas WebCoins e a loja.')}${mensagem()}`, 'conta-dlg');
  if (!perfil) return dialogo('Loja WebFoot', `<p class="cinza">Carregando as suas WebCoins…</p>${mensagem()}`, 'conta-dlg');
  const p = perfil;
  const extrato = p.extrato
    .map((l) => `<li><span class="${l.valor >= 0 ? 'coin-mais' : 'coin-menos'}">${l.valor >= 0 ? '+' : ''}${fmtCoins(l.valor)}</span><span>${esc(l.motivo)}</span><span class="cinza peq">${dataHoraBR(l.t)}</span></li>`)
    .join('');
  return dialogo(
    'Loja WebFoot',
    `<div class="loja-cab"><span class="loja-moeda">${icone('moeda', 54)}</span>
      <div><div class="cinza">Loja WebFoot · seu saldo</div><div class="loja-saldo">${fmtCoins(p.webcoins)} <span>WebCoins</span></div></div>
      <div class="loja-conta">${avatarHtml(p.foto, p.usuario, 34)}<span>${esc(p.usuario)}</span></div></div>
    <div class="pn-tit verde"><span>Como ganhar WebCoins</span></div>
    <p class="cinza">As WebCoins são ganhas ao realizar certas conquistas dentro do jogo. As regras serão anunciadas em breve.</p>
    <div class="pn-tit"><span>Itens da loja</span></div>
    ${itensDaLoja(p.webcoins)}
    <div class="pn-tit"><span>Extrato</span></div>
    <ul class="coin-extrato">${extrato || '<li class="cinza">Nenhuma movimentação ainda.</li>'}</ul>${mensagem()}`,
    'conta-dlg loja-dlg',
  );
}

// ---- ações ---------------------------------------------------------------------------------------------

function campoTexto(id: string): string {
  return (document.getElementById(id) as HTMLInputElement | null)?.value.trim() ?? '';
}

async function enviarFoto(foto: string | null): Promise<void> {
  const s = sessaoDaConta();
  if (!s) return;
  try {
    await chamarApi('/conta/foto', { token: s.token, foto });
    await carregarPerfil();
    app.mensagem = foto === null ? 'Foto removida.' : 'Foto atualizada.';
  } catch (erro) {
    app.mensagem = texto(erro);
  }
  desenharTela();
}

/** Reduz a imagem escolhida para um quadrado de 96 px (jpeg) e guarda no perfil. */
export function contaArquivo(arquivo: File): void {
  if (!arquivo.type.startsWith('image/') || arquivo.size > 8_000_000) {
    app.mensagem = 'Escolha uma imagem (até 8 MB).';
    desenharTela();
    return;
  }
  const url = URL.createObjectURL(arquivo);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    const lado = Math.min(img.naturalWidth, img.naturalHeight);
    const tela = document.createElement('canvas');
    tela.width = 96;
    tela.height = 96;
    const ctx = tela.getContext('2d');
    if (!ctx || lado <= 0) {
      app.mensagem = 'Não consegui ler essa imagem.';
      desenharTela();
      return;
    }
    ctx.drawImage(img, (img.naturalWidth - lado) / 2, (img.naturalHeight - lado) / 2, lado, lado, 0, 0, 96, 96);
    void enviarFoto(tela.toDataURL('image/jpeg', 0.82));
  };
  img.onerror = () => {
    URL.revokeObjectURL(url);
    app.mensagem = 'Não consegui ler essa imagem.';
    desenharTela();
  };
  img.src = url;
}

export function contaClique(nome: string, d: DOMStringMap): void {
  app.mensagem = '';
  if (nome === 'conta-aba') {
    aba = d.p === 'conquistas' ? 'conquistas' : d.p === 'foto' ? 'foto' : d.p === 'convite' ? 'convite' : d.p === 'amigos' ? 'amigos' : 'resumo';
    if (aba === 'amigos') void carregarAmigos().then(desenharTela);
    return;
  }
  if (nome === 'conta-amigo-pedir') {
    const alvo = campoTexto('amigo-novo').trim();
    if (!alvo) {
      app.mensagem = 'Digite o usuário do técnico que você quer adicionar.';
      return;
    }
    void acaoAmigo('pedir', alvo);
    return;
  }
  if (nome === 'conta-amigo') {
    void acaoAmigo(d.acao ?? '', d.u ?? '');
    return;
  }
  if (nome === 'conta-copiar-convite') {
    const link = linkDeConvite(perfil?.convite?.codigo ?? '');
    const campo = document.getElementById('conta-link-convite') as HTMLInputElement | null;
    const ok = (): void => {
      app.mensagem = 'Link copiado! Mande para quem quiser convidar.';
      desenharTela();
    };
    const falhou = (): void => {
      campo?.select();
      app.mensagem = 'Não consegui copiar sozinho: o link está selecionado, copie com Ctrl+C.';
      desenharTela();
    };
    if (navigator.clipboard?.writeText) void navigator.clipboard.writeText(link).then(ok, falhou);
    else falhou();
    return;
  }
  if (nome === 'conta-entrar' || nome === 'conta-registrar') {
    void entrarNaConta(nome === 'conta-entrar' ? 'entrar' : 'registrar', campoTexto('cx-usuario'), (document.getElementById('cx-senha') as HTMLInputElement | null)?.value ?? '', campoTexto('cx-email')).then(
      async (estado) => {
        if (estado === 'confirmar') {
          app.mensagem = 'Enviamos um código de 6 dígitos para o seu e-mail: digite abaixo.';
        } else {
          await carregarPerfil();
          app.mensagem = `Bem-vindo, ${sessaoDaConta()?.usuario ?? ''}!`;
        }
        desenharTela();
      },
      (erro) => {
        app.mensagem = texto(erro);
        desenharTela();
      },
    );
    return;
  }
  // etapa do código de 6 dígitos (conta nova ou ainda não confirmada); serve também para o formulário da tela online
  if (nome === 'conta-email-confirmar') {
    void confirmarEmailDaConta(campoTexto('cx-codigo')).then(
      async () => {
        await carregarPerfil();
        app.mensagem = `Bem-vindo, ${sessaoDaConta()?.usuario ?? ''}! Seu e-mail foi confirmado.`;
        desenharTela();
      },
      (erro) => {
        app.mensagem = texto(erro);
        desenharTela();
      },
    );
    return;
  }
  if (nome === 'conta-email-reenviar') {
    void reenviarCodigoDaConta().then(
      (email) => {
        app.mensagem = `Código novo enviado${email ? ` para ${email}` : ''}.`;
        desenharTela();
      },
      (erro) => {
        app.mensagem = texto(erro);
        desenharTela();
      },
    );
    return;
  }
  if (nome === 'conta-email-cancelar') {
    cancelarConfirmacao();
    return;
  }
  if (nome === 'conta-sair') {
    sairDaConta();
    perfil = null;
    app.dialogo = null;
    return;
  }
  if (nome === 'conta-foto-preset') {
    void enviarFoto(`preset:${Number(d.i)}`);
    return;
  }
  if (nome === 'conta-comprar') {
    void comprarItem(String(d.id ?? ''));
    return;
  }
  if (nome === 'conta-foto-remover') {
    void enviarFoto(null);
    return;
  }
  if (nome === 'conta-foto-enviar') {
    (document.getElementById('conta-foto-arquivo') as HTMLInputElement | null)?.click();
  }
}
